/**
 * ScrollToTop — resets window scroll to (0, 0) on every route change.
 *
 * React Router (client-side navigation) preserves the scroll position
 * from the previous page, which means moving from a long calculator
 * page to a shorter tool leaves you mid-scroll on the new page.
 *
 * Mount this once inside <BrowserRouter> and every subsequent
 * `pathname` change will jump the viewport back to the top.
 *
 * Notes:
 *   - Uses `behavior: 'auto'` (instant) — animated scrolls feel odd
 *     when the destination content hasn't rendered yet.
 *   - Also resets `document.documentElement.scrollTop` +
 *     `document.body.scrollTop` for browsers that don't honour
 *     `window.scrollTo` reliably (some mobile Safari builds).
 *   - Respects `#hash` anchors — if the new URL has one we skip the
 *     reset so anchor jumps still work (e.g. `/#features`).
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();

  useEffect(() => {
    if (hash) return;   // let the browser jump to the anchor
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    } catch (_) {
      window.scrollTo(0, 0);
    }
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
  }, [pathname, hash]);

  return null;
};

export default ScrollToTop;
